import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";

// Angular modules
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";

// Material MDC Imports
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonToggleModule } from "@angular/material/button-toggle";

// CDK Imports
import { OverlayModule } from "@angular/cdk/overlay";

import {
  IGeofencingSearchValidation,
  InputActionResult,
  MapActionRequest,
  SearchDisplayMode,
  SearchInputState,
  SearchInputType,
  SearchState,
  SearchSuggestion,
  SearchTypeMode,
} from "../model";
import {
  GeofencingSearchFilterService,
  SearchModeCoordinator,
  SearchQueryService,
  SearchInputService,
} from "../services/search";
import { MatDividerModule } from "@angular/material/divider";
import { FeatureFlagsService } from "../../viewer-components/services/internal-services/feature-flags.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { FeatureFlags } from "../../viewer-components/model/feature-flags";
import { SuggestionPanelComponent } from "./suggestion-panel/suggestion-panel.component";
import { combineLatest, debounceTime, Observable, shareReplay } from "rxjs";
import { MapActionsService } from "../../shared/services/map-actions.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatMenuModule } from "@angular/material/menu";

/**
 * Enhanced search component with toolbar layout, floating overlays, and configurable display modes
 */
@Component({
  selector: "app-compound-search-enhanced",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./search-enhanced.component.html",
  styleUrls: ["./search-enhanced.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatDividerModule,
    OverlayModule,
    SuggestionPanelComponent,
    MatMenuModule,
  ],
})
export class SearchEnhancedComponent implements OnInit {
  public readonly searchQueryService = inject(SearchQueryService);

  private ngZone = inject(NgZone);

  private destroyRef = inject(DestroyRef);

  private changeDetectorRef = inject(ChangeDetectorRef);

  featureFlagService = inject(FeatureFlagsService);

  private searchInputService = inject(SearchInputService);

  public readonly geofencingFilterService = inject(GeofencingSearchFilterService);

  private searchModeCoordinator = inject(SearchModeCoordinator);

  private mapActionsService = inject(MapActionsService);

  private breakpointObserver = inject(BreakpointObserver);

  @ViewChild("searchInput", { static: false }) searchInputRef!: ElementRef<HTMLInputElement>;

  /** Output event for filter panel toggle */
  @Output() filterPanelToggle = new EventEmitter<boolean>();

  FeatureFlags = FeatureFlags;

  /** Form group for reactive forms */
  searchForm: FormGroup;

  /** Current search state */
  searchState: SearchState;

  /** Whether autocomplete overlay is visible */
  showAutocompleteOverlay = false;

  /** Index of the keyboard-highlighted suggestion (-1 = none) */
  activeSuggestionIndex = -1;

  /** Whether filter configuration panel is open */
  filterPanelOpen = false;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
    map((result) => result.matches),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  /** Observable for whether search should be disabled */

  isSearchDisabled$ = this.searchQueryService.searchDisplayMode$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map((displayMode) => displayMode !== SearchDisplayMode.GRID && displayMode !== SearchDisplayMode.MAP),
  );

  /**
   * Observable for the geofencing precondition of the embeddings search: an area is mandatory
   * and it must stay under the maximum the API can process.
   */
  geofencingValidation$: Observable<IGeofencingSearchValidation> = this.searchQueryService.geofencingValidation$;

  /** Observable for the tooltip of the search button — explains why it is blocked, when it is */
  searchButtonTooltip$: Observable<string> = this.geofencingValidation$.pipe(
    map((validation) => validation.reason ?? "Search"),
  );

  /** Observable for the tooltip of the geofencing button, including the measured area when available */
  geofencingButtonTooltip$: Observable<string> = this.geofencingValidation$.pipe(
    map((validation) =>
      validation.area ? `Geofencing area: ${validation.area.formatted}` : "Config Geofencing",
    ),
  );

  /** Observable for whether the text search button should be disabled — embeddings search needs geofencing */
  isTextSearchDisabled$ = combineLatest([this.isSearchDisabled$, this.geofencingValidation$]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(([displayDisabled, geofencing]) => displayDisabled || !geofencing.valid),
  );

  /** Observable for whether location search button should be disabled */
  isLocationSearchDisabled$ = combineLatest([
    this.searchQueryService.searchDisplayMode$,
    this.geofencingValidation$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(
      ([displayMode, geofencing]) =>
        !geofencing.valid ||
        !this.searchModeCoordinator.validateSearchTypeMode(SearchTypeMode.LOCATION_SEARCH, displayMode).valid,
    ),
  );

  /** Observable for whether image search button should be disabled */
  isImageSearchDisabled$ = combineLatest([
    this.searchQueryService.searchDisplayMode$,
    this.geofencingValidation$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(
      ([displayMode, geofencing]) =>
        !geofencing.valid ||
        !this.searchModeCoordinator.validateSearchTypeMode(SearchTypeMode.IMAGE_SEARCH, displayMode).valid,
    ),
  );

  /** Observable for text search tooltip text — surfaces the geofencing reason when that's what's blocking it */
  textSearchTooltip$ = this.geofencingValidation$.pipe(
    map((geofencing) => (geofencing.valid ? "Search by text" : geofencing.reason ?? "Search by text")),
  );

  /** Observable for location search tooltip text */
  locationSearchTooltip$ = combineLatest([
    this.searchQueryService.searchDisplayMode$,
    this.geofencingValidation$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(([displayMode, geofencing]) => {
      if (!geofencing.valid) return geofencing.reason ?? "Location search not available";
      const validation = this.searchModeCoordinator.validateSearchTypeMode(SearchTypeMode.LOCATION_SEARCH, displayMode);
      return validation.valid ? "Search by location" : validation.reasons?.[0] || "Location search not available";
    }),
  );

  /** Observable for image search tooltip text */
  imageSearchTooltip$ = combineLatest([
    this.searchQueryService.searchDisplayMode$,
    this.geofencingValidation$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(([displayMode, geofencing]) => {
      if (!geofencing.valid) return geofencing.reason ?? "Image search not available";
      const validation = this.searchModeCoordinator.validateSearchTypeMode(SearchTypeMode.IMAGE_SEARCH, displayMode);
      return validation.valid ? "Search by image" : validation.reasons?.[0] || "Image search not available";
    }),
  );

  /** Whether search is currently loading */
  isLoading = false;

  /** Whether a search execution is already in progress (guards against concurrent calls) */
  private isExecutingSearch = false;

  /** Input focus state */
  isFocused = false;

  /** Current input state from unified service */
  public inputState: SearchInputState = {
    inputText: "",
    detectedType: SearchInputType.TEXT,
    confidence: 0,
    isValid: true,
    loading: false,
    lastAnalyzed: 0,
    currentSuggestions: [],
  };

  /** Kept in sync with geofencingValidation$ — read synchronously to filter suggestions */
  private hasValidGeofencing = false;

  /**
   * Suggestions actually shown to the user. Text suggestions trigger an embeddings search, which
   * needs geofencing — without it, only address suggestions (a map action, not a search) remain.
   */
  get visibleSuggestions(): SearchSuggestion[] {
    if (this.hasValidGeofencing) {
      return this.inputState.currentSuggestions;
    }
    return this.inputState.currentSuggestions.filter((s) => s.type === SearchInputType.GOOGLE_PLACE);
  }

  /** Array of rotating placeholder texts */
  private placeholderTexts = [
    "Search for properties...",
    "Enter an address or coordinates...",
    "Find imagery by location...",
    "Search by property features...",
    "Discover nearby assets...",
    "Try '40.7128,-74.0060' for coordinates...",
    "Search 'New York' for places...",
    "Enter WKT geometry for boundaries...",
  ];

  /** Current placeholder text */
  currentPlaceholder = this.placeholderTexts[0];

  /** Placeholder rotation interval */
  private placeholderInterval?: ReturnType<typeof setInterval>;

  enableSearch = false;

  public SearchTypeMode = SearchTypeMode;

  public SearchDisplayMode = SearchDisplayMode;

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeToSearchState();
    this.subscribeToSearchDisabled();
    this.subscribeToFeatureService();
    this.subscribeToGeofencingValidation();
    this.startPlaceholderRotation();

    this.initializeSearchFlow();
    // Set up cleanup when component is destroyed
    this.destroyRef.onDestroy(() => {
      if (this.placeholderInterval) {
        clearInterval(this.placeholderInterval);
      }
    });
  }

  /**
   * Initializes the reactive form
   */
  private initializeForm(): void {
    this.searchForm = new FormGroup({
      query: new FormControl(""),
      maxRecords: new FormControl(20),
    });
  }

  /**
   * Initialize the unified search flow
   * Sets up input processing with debounce and suggestions
   */
  private initializeSearchFlow(): void {
    // Subscribe to input changes and process with debounce
    this.queryForm.valueChanges
      .pipe(startWith(""), debounceTime(100), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((input) => {
        // Get current display mode and process input
        if (input !== "") {
          const currentDisplayMode = this.searchQueryService.getCurrentDisplayMode();
          this.searchInputService.processInput(input || "", currentDisplayMode);
        }
      });

    // Subscribe to input state changes
    this.searchInputService.inputState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.inputState = state;
      // Gate on isFocused — prevents debounced suggestions from reopening the overlay after blur.
      // Also opens once the user has typed something without a valid geofencing, so the warning
      // row shows even when every suggestion got filtered out (see visibleSuggestions).
      this.showAutocompleteOverlay =
        this.isFocused &&
        (this.visibleSuggestions.length > 0 || (!this.hasValidGeofencing && !!state.inputText));
      if (!this.showAutocompleteOverlay) {
        this.activeSuggestionIndex = -1;
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  // /**
  //  * Subscribes to search state changes
  //  */
  private subscribeToSearchState(): void {
    this.searchQueryService.searchState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.searchState = state;
      this.isLoading = state?.loading || false;
      this.hideAutocomplete();
      this.changeDetectorRef.detectChanges();
    });
  }

  private subscribeToSearchDisabled(): void {
    this.isSearchDisabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isDisabled) => {
      if (isDisabled) {
        this.queryForm.disable({ emitEvent: false });
      } else {
        this.queryForm.enable({ emitEvent: false });
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  private subscribeToFeatureService(): void {
    this.featureFlagService
      .getFlags$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags) => {
        this.enableSearch = flags[FeatureFlags.ENHANCED_GEO_SEARCH] || false;
      });
  }

  private subscribeToGeofencingValidation(): void {
    this.geofencingValidation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((validation) => {
      this.hasValidGeofencing = validation.valid;
      this.changeDetectorRef.detectChanges();
    });
  }

  /**
   * Handle suggestion selection from autocomplete
   * Executes action based on suggestion type
   */
  async onSuggestionSelect(suggestion: SearchSuggestion): Promise<void> {
    this.queryForm.setValue(suggestion.text, { emitEvent: false });
    this.activeSuggestionIndex = -1;

    // Hide autocomplete
    this.hideAutocomplete();

    try {
      // Execute suggestion action
      const result = await this.searchInputService.selectSuggestion(suggestion);
      this.handleActionResult(result);
    } catch (error) {
      console.error("❌ Error executing suggestion:", error);
    } finally {
      this.searchInputRef?.nativeElement?.focus();
    }
  }

  /**
   * Execute search from button click or Enter key
   * Uses current input state for type detection
   */
  async onSearchExecute(): Promise<void> {
    const input = this.queryForm?.value?.trim();

    if (!input || this.isExecutingSearch) {
      return;
    }

    this.isExecutingSearch = true;

    // Hide autocomplete
    this.hideAutocomplete();

    try {
      const result = await this.searchInputService.executeInputSearch();

      this.handleActionResult(result);
    } catch (error) {
      console.error("❌ Error executing search:", error);
    } finally {
      this.isExecutingSearch = false;
    }
  }

  /**
   * Handle action result from search or suggestion
   */
  private handleActionResult(result: InputActionResult): void {
    if (!result.success) {
      console.error(result.message);
    } else if (result.shouldTriggerBackendSearch) {
      // Text search is an embeddings search and needs geofencing — the mode buttons and Search
      // button are already disabled for this, but Enter/suggestion-select bypass those controls
      // and call straight into this handler, so the check is repeated here.
      if (!this.searchQueryService.getCurrentGeofencingValidation().valid) {
        return;
      }
      // Trigger server search via SearchQueryService
      const input = this.queryForm.value?.trim();
      if (input) {
        this.searchQueryService.performTextSearch(input).subscribe(() => {});
      }
    } else {
      if (result.actionType === "map_action" || result.actionType === "place_lookup") {
        const action: MapActionRequest = {
          type: "search",
          data: result.data,
        };
        this.mapActionsService.requestMapAction(action);
      }
    }
  }

  /**
   * Clear search and reset state
   */
  onSearchClear(): void {
    const searchControl = this.searchForm.get("searchQuery");
    if (searchControl) {
      searchControl.setValue("", { emitEvent: true });
    }

    this.searchInputService.clearState();
    this.hideAutocomplete();
    this.searchInputRef?.nativeElement?.focus();
  }

  /**
   * Hide autocomplete overlay
   */
  private hideAutocomplete(): void {
    this.showAutocompleteOverlay = false;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Track by function for autocomplete suggestions
   * @param index Index of the item
   * @param suggestion The suggestion item
   */
  trackBySuggestion(index: number, suggestion: SearchSuggestion): string {
    return suggestion.text;
  }

  /**
   * Handles view change from mat-button-toggle-group
   * @param mode  SearchDisplayMode event
   */
  onViewChange(mode: SearchDisplayMode): void {
    // Update the display mode through the service
    this.searchQueryService.setSearchDisplayMode(mode);

    // Trigger change detection
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Handles search mode change from search type buttons
   * @param mode The search type mode to set
   */
  changeSearchMode(mode: SearchTypeMode): void {
    switch (mode) {
      case SearchTypeMode.TEXT_SEARCH:
        this.searchQueryService.setSearchTypeMode(mode);
        break;
      case SearchTypeMode.LOCATION_SEARCH:
        this.searchQueryService.setSearchTypeMode(mode);
        break;
      case SearchTypeMode.IMAGE_SEARCH:
        this.searchQueryService.setSearchTypeMode(mode);
        break;
    }
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Handles input focus event — always triggers suggestion generation so the
   * overlay reopens correctly when the user re-focuses with existing text.
   */
  onFocus(): void {
    this.isFocused = true;
    const currentDisplayMode = this.searchQueryService.getCurrentDisplayMode();
    this.searchInputService.processInput(this.searchValue || "", currentDisplayMode);
  }

  /**
   * Handles input blur event.
   * Cancels any pending debounce first so late suggestions cannot reopen the overlay,
   * then delays the actual close to allow mousedown → click events on suggestions to fire.
   */
  onBlur(): void {
    this.searchInputService.cancelPendingSuggestions();
    this.activeSuggestionIndex = -1;
    setTimeout(() => {
      this.isFocused = false;
      this.showAutocompleteOverlay = false;
      this.changeDetectorRef.detectChanges();
    }, 200);
  }

  get queryForm(): FormControl {
    return this.searchForm.get("query") as FormControl;
  }

  /**
   * Unified keyboard handler for the search input.
   *
   * When the autocomplete overlay is open:
   *   - ArrowDown / ArrowUp  → cycle through suggestions
   *   - Enter                → select the highlighted suggestion, or execute search if none highlighted
   *   - Escape               → close the overlay without clearing the input
   *
   * When the overlay is closed:
   *   - Enter                → execute search
   *   - Escape               → clear the input (same as the clear button)
   */
  onKeyDown(event: KeyboardEvent): void {
    const suggestions = this.visibleSuggestions;

    if (this.showAutocompleteOverlay && suggestions.length > 0) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % suggestions.length;
          this.changeDetectorRef.detectChanges();
          break;
        case "ArrowUp":
          event.preventDefault();
          this.activeSuggestionIndex =
            this.activeSuggestionIndex <= 0 ? suggestions.length - 1 : this.activeSuggestionIndex - 1;
          this.changeDetectorRef.detectChanges();
          break;
        case "Enter":
          event.preventDefault();
          if (this.activeSuggestionIndex >= 0) {
            this.onSuggestionSelect(suggestions[this.activeSuggestionIndex]);
          } else {
            this.onSearchExecute();
          }
          break;
        case "Escape":
          event.preventDefault();
          this.activeSuggestionIndex = -1;
          this.hideAutocomplete();
          break;
        default:
          break;
      }
    } else {
      if (event.key === "Enter") {
        this.onSearchExecute();
      } else if (event.key === "Escape") {
        this.onSearchClear();
      }
    }
  }

  /**
   * Gets current search value
   */
  get searchValue(): string {
    return this.queryForm?.value || "";
  }

  /**
   * Checks if search has value
   */
  get hasSearchValue(): boolean {
    return this.searchValue.trim().length > 0;
  }

  /**
   * Clears the search input and any active image search context
   */
  clearSearch(): void {
    this.queryForm?.setValue("");
    this.showAutocompleteOverlay = false;
    this.searchInputRef?.nativeElement?.focus();

    // Reset search type mode to default (text)
    this.searchQueryService.setSearchTypeMode(SearchTypeMode.TEXT_SEARCH);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Starts the placeholder text rotation
   */
  private startPlaceholderRotation(): void {
    let index = 0;
    this.placeholderInterval = setInterval(() => {
      this.ngZone.run(() => {
        index = (index + 1) % this.placeholderTexts.length;
        this.currentPlaceholder = this.placeholderTexts[index];
        this.changeDetectorRef.detectChanges();
      });
    }, 4000); // Rotate every 4 seconds
  }

  /**
   * Opens the geofencing configuration dialog — actual workflow lives in
   * {@link GeofencingSearchFilterService}, shared with the search-settings panel.
   */
  openGeofencingDialog(): void {
    this.geofencingFilterService.openDialog({});
  }

}
